// DropDetail with YouTube embed, transcript, related links, drop comments that cross-post to community.
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Bookmark, Hourglass, ChevronLeft, CheckCircle2, MessageCircle, ExternalLink, Send, Trash2, Maximize2, X, Clock, Boxes, Link2 } from "lucide-react";
import { api, fmt } from "../lib/api";
import { Avatar, SmartImage } from "../components/SmartImage";
import { TangibleAsset, dropVariant } from "../components/TangibleAsset";
import ReadingProgress from "../components/ReadingProgress";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

function youtubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
}

export function DropDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentBody, setCommentBody] = useState("");
  const [crossPost, setCrossPost] = useState(true);
  const [flowOpen, setFlowOpen] = useState(false);

  const load = () => api.get(`/drops/${id}`).then((r) => {
    setData(r.data);
    setNoteBody(r.data?.note?.body || "");
  }).catch((e) => { console.error("Drop detail load failed", e); });
  const loadComments = () => api.get(`/drops/${id}/comments`).then((r) => setComments(r.data.comments));

  useEffect(() => { load(); loadComments(); }, [id]); // eslint-disable-line

  if (!data) return <div className="min-h-screen flex items-center justify-center"><Hourglass className="w-6 h-6 text-gold animate-glow"/></div>;
  const d = data.drop;
  const yt = youtubeId(d.youtube_url);

  const toggleBookmark = async () => {
    const { data: r } = await api.post(`/bookmarks/${id}`);
    load();
    toast.success(r.bookmarked ? "Bookmarked" : "Removed bookmark");
  };
  const copyLink = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
      else throw new Error("no clipboard api");
      toast.success("Link copied");
    } catch (e) {
      try {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
        toast.success("Link copied");
      } catch (e2) { console.error("Copy failed", e2); toast.error("Couldn't copy"); }
    }
  };
  const saveNote = async () => {
    setBusy(true);
    try { await api.post("/notes", { drop_id: id, body: noteBody }); toast.success("Note saved (private)"); }
    catch (e) { console.error("Save note failed", e); toast.error("Could not save"); }
    setBusy(false);
  };
  const submitQuiz = async () => {
    if (!data.quiz) return;
    const answers = data.quiz.questions.map((_, i) => quizAnswers[i] ?? -1);
    if (answers.every((a) => a < 0)) { toast.error("Answer at least one to see your result"); return; }
    const { data: r } = await api.post(`/quizzes/${data.quiz.id}/attempt`, { answers });
    setQuizResult(r);
    toast.success(r.result ? `Your result: ${r.result.label}` : "Result ready");
  };
  const postComment = async () => {
    if (!commentBody.trim()) return;
    try {
      await api.post(`/drops/${id}/comments`, { body: commentBody, cross_post_to_community: crossPost });
      setCommentBody("");
      loadComments();
      toast.success(crossPost ? "Posted & shared to community" : "Posted on this drop");
    } catch (e) { console.error("Post comment failed", e); toast.error("Could not post"); }
  };
  const delComment = async (cid) => {
    if (!window.confirm("Delete comment?")) return;
    await api.delete(`/drops/comments/${cid}`); loadComments();
  };

  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
      <ReadingProgress />
      <div className="max-w-4xl mx-auto">
        <button onClick={() => nav(-1)} className="overline flex items-center gap-2 mb-6"><ChevronLeft className="w-4 h-4"/>Back</button>
        <div className="flex items-start gap-6">
          <TangibleAsset variant={dropVariant(d)} title={d.title} width="w-24 shrink-0 hidden sm:block" />
          <div>
            <div className="overline mb-3">{d.foundational ? "FOUNDATIONAL" : "TRANSMISSION"} {d.quick_win && "// QUICK WIN"}</div>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-cream leading-[1.05]">{d.title}</h1>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-4 flex-wrap">
          <span className="text-xs font-mono text-textDim">{fmt.datetime(d.published_at || d.scheduled_for)}</span>
          {d.expires_at && (
            <span className="text-xs font-mono text-gold flex items-center gap-1" data-testid="drop-expiry"><Clock className="w-3 h-3"/>Available until {fmt.date(d.expires_at)}</span>
          )}
          {d.flow_embed && (
            <span className="overline text-gold flex items-center gap-1"><Boxes className="w-3 h-3"/>FLOW</span>
          )}
          <button data-testid="bookmark-toggle" onClick={toggleBookmark} className="text-xs font-mono text-textMuted hover:text-gold flex items-center gap-2">
            <Bookmark className={`w-4 h-4 ${data.bookmarked ? "fill-gold text-gold" : ""}`}/>{data.bookmarked ? "Bookmarked" : "Bookmark"}
          </button>
          <button data-testid="copy-link" onClick={copyLink} className="text-xs font-mono text-textMuted hover:text-gold flex items-center gap-2">
            <Link2 className="w-4 h-4"/>Share
          </button>
        </div>

        {d.flow_embed === "link" && d.flow_url && (
          <div className="mt-8 panel p-6 sm:p-8 border-gold" data-testid="flow-link">
            <div className="overline mb-2">// EXTERNAL FLOW</div>
            {d.insight_preview && <p className="text-cream/90 mb-4">{d.insight_preview}</p>}
            <a href={d.flow_url} target="_blank" rel="noreferrer" className="btn-gold inline-flex" data-testid="flow-open"><ExternalLink className="w-4 h-4"/>Open {d.title}</a>
          </div>
        )}

        {d.flow_embed === "iframe" && d.flow_url && (
          <div className="mt-8" data-testid="flow-iframe">
            <div className="flex items-center justify-between mb-2">
              <div className="overline">// INTERACTIVE FLOW</div>
              <button onClick={() => setFlowOpen(true)} className="btn-ghost text-xs" data-testid="flow-expand"><Maximize2 className="w-3 h-3"/>Expand</button>
            </div>
            <div className="aspect-video w-full border border-borderGold bg-surface">
              <iframe src={d.flow_url} title={d.title} className="w-full h-full" allow="fullscreen; clipboard-write; encrypted-media" />
            </div>
          </div>
        )}

        {flowOpen && d.flow_url && (
          <div className="fixed inset-0 z-[100] bg-void/95 flex flex-col" data-testid="flow-fullscreen">
            <div className="flex items-center justify-between px-4 py-3 border-b border-borderGold">
              <div className="overline truncate">// {d.title}</div>
              <button onClick={() => setFlowOpen(false)} className="btn-ghost text-xs" data-testid="flow-close"><X className="w-4 h-4"/>Close</button>
            </div>
            <iframe src={d.flow_url} title={d.title} className="flex-1 w-full" allow="fullscreen; clipboard-write; encrypted-media" />
          </div>
        )}

        {yt && (
          <div className="mt-8 aspect-video w-full border border-borderGold bg-surface">
            <iframe
              src={`https://www.youtube.com/embed/${yt}?rel=0&modestbranding=1`}
              title={d.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="w-full h-full"
              data-testid="drop-youtube"
            />
          </div>
        )}

        {d.media_url && !yt && (
          <div className="mt-8 border border-borderGold">
            <SmartImage src={d.media_url} alt="" className="w-full max-h-[480px] object-cover"/>
          </div>
        )}

        <div className="mt-10 prose prose-invert max-w-none">
          <article className="whitespace-pre-wrap font-body text-base sm:text-lg leading-[1.8] text-cream/90">{d.body_md}</article>
        </div>

        {d.related_links && d.related_links.length > 0 && (
          <div className="mt-10 panel p-6">
            <div className="overline mb-3">// RELATED LINKS</div>
            <ul className="space-y-2">
              {d.related_links.map((l) => (
                  <li key={l.url || l.title}><a href={l.url} target="_blank" rel="noreferrer" className="text-gold hover:underline flex items-center gap-2"><ExternalLink className="w-3 h-3"/>{l.title || l.url}</a></li>
                ))}
            </ul>
          </div>
        )}

        {d.transcript_md && (
          <details className="mt-10 panel p-6">
            <summary className="overline cursor-pointer">// TRANSCRIPT (click to expand)</summary>
            <div className="mt-4 whitespace-pre-wrap text-cream/85 leading-relaxed text-sm">{d.transcript_md}</div>
          </details>
        )}

        {data.quiz && (
          <div className="mt-16 panel p-6 sm:p-8" data-testid="diagnostic-block">
            <div className="overline mb-3">// SELF-DIAGNOSTIC · no right or wrong, just a mirror</div>
            <h2 className="font-display text-2xl sm:text-3xl text-cream mb-3">{data.quiz.title}</h2>
            {data.quiz.intro && <p className="text-textMuted mb-6">{data.quiz.intro}</p>}
            <div className="space-y-6">
              {data.quiz.questions.map((q, i) => (
                <div key={q.id || `${q.q}-${i}`}>
                  <div className="text-cream mb-2">{i + 1}. {q.q}</div>
                  <div className="space-y-2">
                    {q.options.map((o, oi) => (
                      <label key={q.id ? `${q.id}-opt-${oi}` : `${q.q}-${oi}`} className="flex items-center gap-3 cursor-pointer">
                        <input type="radio" name={`q${i}`} checked={quizAnswers[i] === oi} onChange={() => setQuizAnswers({ ...quizAnswers, [i]: oi })} style={{width:16,height:16}}/>
                        <span className="text-textMuted">{typeof o === "string" ? o : o.text}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button data-testid="quiz-submit" onClick={submitQuiz} className="btn-gold mt-6">{quizResult ? "Re-examine" : "Reveal my result"}</button>
            {quizResult && quizResult.result && (
              <div className="mt-6 panel p-6 border-gold" data-testid="diagnostic-result">
                <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-4 h-4 text-gold"/><span className="overline">// WHERE YOU ARE RIGHT NOW</span></div>
                <h3 className="font-display text-3xl text-gold">{quizResult.result.label}</h3>
                <p className="text-cream/90 mt-3 whitespace-pre-wrap">{quizResult.result.description}</p>
                {quizResult.result.scripture && <p className="font-display italic text-lg text-gold/90 mt-4">{quizResult.result.scripture}</p>}
                {quizResult.result.growth && (
                  <div className="mt-4 pt-4 border-t border-borderGold">
                    <div className="overline mb-1">// YOUR NEXT STEP</div>
                    <p className="text-cream/90">{quizResult.result.growth}</p>
                  </div>
                )}
                {quizResult.types?.length > 1 && (
                  <div className="mt-5 pt-4 border-t border-borderGold">
                    <div className="overline mb-2">// HOW YOU LEANED</div>
                    <div className="space-y-1">
                      {quizResult.types.map((t) => {
                        const n = quizResult.tallies?.[t.key] || 0;
                        const pct = quizResult.answered ? Math.round((n / quizResult.answered) * 100) : 0;
                        return (
                          <div key={t.key} className="flex items-center gap-3 text-sm">
                            <div className="w-32 text-textMuted shrink-0 truncate">{t.label}</div>
                            <div className="flex-1 h-2 bg-borderGold relative overflow-hidden"><div className="absolute inset-y-0 left-0 bg-gold" style={{ width: `${pct}%` }}></div></div>
                            <div className="w-10 text-right font-mono text-textDim">{pct}%</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <p className="text-textDim text-xs font-mono mt-5 pt-4 border-t border-borderGold">This is a mirror for the season you're in right now — not a label for life. Seasons shift, warfare comes and goes, and what's true today can change as God forms you. Take the next step, and come back when the season turns.</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-16">
          <div className="overline mb-3">// YOUR PRIVATE NOTE</div>
          <p className="text-textDim text-xs font-mono mb-3">Only you can see this. Not posted to community.</p>
          <textarea data-testid="note-textarea" rows={5} value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder="What landed? What do you need to act on?"/>
          <button data-testid="note-save" onClick={saveNote} disabled={busy} className="btn-gold mt-4">Save note</button>
        </div>

        <div className="mt-16">
          <div className="overline mb-3">// DROP DISCUSSION</div>
          <h3 className="font-display text-2xl text-cream mb-2">Engage on this drop</h3>
          <p className="text-textDim text-xs font-mono mb-4">Comments here are public. Tick the box to also cross-post to the community feed.</p>
          <div className="space-y-3">
            {comments.map((c) => (
              <div key={c.id} className="panel p-4">
                <div className="flex items-start gap-3">
                  <Link to={`/u/${c.user_id}`} className="w-9 h-9 border border-borderGold overflow-hidden shrink-0 bg-surface">
                    <Avatar src={c.user_avatar} name={c.user_name} />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/u/${c.user_id}`} className="text-cream font-medium hover:text-gold">{c.user_name}</Link>
                      {c.user_role === "admin" && <span className="overline">// ROBIN</span>}
                      <span className="text-xs font-mono text-textDim">{fmt.datetime(c.created_at)}</span>
                    </div>
                    <p className="text-cream/90 whitespace-pre-wrap mt-1">{c.body}</p>
                  </div>
                  {(user?.role === "admin" || c.user_id === user?.id) && (
                    <button onClick={() => delComment(c.id)} className="text-textMuted hover:text-ruby shrink-0"><Trash2 className="w-4 h-4"/></button>
                  )}
                </div>
              </div>
            ))}
            {comments.length === 0 && <p className="text-textMuted text-sm">No comments yet.</p>}
          </div>
          <div className="mt-5 panel p-4">
            <textarea data-testid="drop-comment-body" rows={3} value={commentBody} onChange={(e) => setCommentBody(e.target.value)} placeholder="Share what landed for you…"/>
            <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-textMuted flex items-center gap-2">
                <input type="checkbox" checked={crossPost} onChange={(e) => setCrossPost(e.target.checked)} style={{width:16,height:16}}/>
                Also share to community
              </label>
              <button data-testid="drop-comment-submit" onClick={postComment} className="btn-gold"><Send className="w-4 h-4"/>Post</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function DropsList() {
  const [drops, setDrops] = useState([]);
  useEffect(() => { api.get("/drops").then((r) => setDrops(r.data.drops || [])); }, []);
  const published = drops.filter((d) => d.published && !d.locked);
  const upcoming = drops.filter((d) => d.preview);
  const locked = drops.filter((d) => d.locked);
  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-10 py-12 sm:py-16">
      <div className="max-w-6xl mx-auto">
        <div className="overline mb-4">// THE ARCHIVE</div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-cream">All drops</h1>
        <p className="text-textMuted mt-3">Saturday after Saturday. The vault stays open while your seat does.</p>
        <h2 className="font-display text-2xl text-cream mt-16 mb-4">Published</h2>
        <div className="space-y-3">
          {published.map((d) => (
            <Link key={d.id} to={`/drops/${d.id}`} className="block panel p-6 hover:border-borderGoldHi" data-testid={`drop-card-${d.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-5">
                  <TangibleAsset variant={dropVariant(d)} title={d.title} width="w-16 shrink-0" />
                  <div>
                    <div className="overline mb-1">
                      {d.flow_embed ? "FLOW" : (d.foundational ? "FOUNDATIONAL" : "FULL")} {d.quick_win && "// QUICK WIN"}
                      {d.expires_at && <span className="text-gold"> // UNTIL {fmt.date(d.expires_at).toUpperCase()}</span>}
                    </div>
                    <h3 className="font-display text-2xl text-cream">{d.title}</h3>
                    {d.insight_preview && <p className="text-textMuted text-sm mt-1">{d.insight_preview}</p>}
                  </div>
                </div>
                <div className="text-xs font-mono text-textDim whitespace-nowrap">{fmt.date(d.published_at || d.scheduled_for)}</div>
              </div>
            </Link>
          ))}
          {published.length === 0 && <p className="text-textMuted">Nothing published yet.</p>}
        </div>
        {upcoming.length > 0 && (
          <>
            <h2 className="font-display text-2xl text-cream mt-16 mb-4">Coming soon</h2>
            <div className="space-y-3">{upcoming.map((d) => (
              <div key={d.id} className="panel p-6 opacity-70">
                <div className="flex items-start gap-5">
                  <TangibleAsset variant={dropVariant(d)} title={d.title} width="w-16 shrink-0" locked />
                  <div>
                    <div className="flex items-center gap-3 mb-1"><Hourglass className="w-4 h-4 text-gold animate-glow"/><div className="overline">SCHEDULED · {fmt.datetime(d.scheduled_for)}</div></div>
                    <h3 className="font-display text-2xl text-cream">{d.title}</h3>
                    {d.insight_preview && <p className="text-textMuted text-sm mt-1">{d.insight_preview}</p>}
                  </div>
                </div>
              </div>))}</div>
          </>
        )}
        {locked.length > 0 && (
          <>
            <h2 className="font-display text-2xl text-cream mt-16 mb-4">A-la-carte</h2>
            <div className="space-y-3">{locked.map((d) => (
              <Link key={d.id} to={`/drops/${d.id}`} className="block panel p-6 hover:border-borderGoldHi">
                <div className="flex items-start gap-5">
                  <TangibleAsset variant={dropVariant(d)} title={d.title} width="w-16 shrink-0" locked />
                  <div>
                    <div className="overline">SINGLE ASSET · {fmt.money(d.alacarte_price_cents)}</div>
                    <h3 className="font-display text-2xl text-cream">{d.title}</h3>
                  </div>
                </div>
              </Link>))}</div>
          </>
        )}
      </div>
    </div>
  );
}

export { DropDetail as default };
function DropDetail_alias() {}
